import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, AlertCircle, Info, Check, Calendar, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { enqueueQuarterlyPriceExtractions } from '@/utils/taskHelpers';
import { useGraphQLHelperV2 } from '@/utils/useGraphQLHelperV2';
import { formatVersionDate, formatVersionDateUS } from '@/utils/dateUtils';
import { buildVersionLabels } from '@/utils/versionOptions';
import { versionToQuarter } from '@/utils/versionUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";

interface VersionInfo {
  version: string;
  quarter: string;
  date: string;
  hasPlans: boolean;
}

interface ExtractQuarterlyPricesModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  slug: string;
  subslug?: string | null;
}

const ExtractQuarterlyPricesModal: React.FC<ExtractQuarterlyPricesModalProps> = ({
  isOpen,
  onOpenChange,
  slug,
  subslug
}) => {
  const { toast } = useToast();
  const [force, setForce] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [versionInfos, setVersionInfos] = useState<VersionInfo[]>([]);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [results, setResults] = useState<{ taskIds: string[], versions: string[] } | null>(null);

  // Get GraphQL data using the helper
  const { graphQLData, isLoading: isGraphQLLoading } = useGraphQLHelperV2(slug);

  // Determine the relevant page based on subslug
  const relevantPage = useMemo(() => {
    if (!graphQLData?.pages || graphQLData.pages.length === 0) return null;
    const pagesWithQuarters = graphQLData.pages.filter(
      p => p.quarters && Array.isArray(p.quarters) && p.quarters.length > 0
    );
    if (pagesWithQuarters.length === 0) return null;

    if (subslug) {
      const cleanInputSubslug = subslug.replace(/^\./, '');
      return (
        pagesWithQuarters.find(p => {
          const cleanPageSubslug = (p.subslug || '').replace(/^\./, '');
          return cleanPageSubslug === cleanInputSubslug;
        }) || null
      );
    }
    return (
      pagesWithQuarters.find(p => !p.subslug || p.subslug === '' || p.subslug === null) ||
      pagesWithQuarters[0] ||
      null
    );
  }, [graphQLData, subslug]);

  // Build version labels using the centralized utility
  const versionLabels = useMemo(() => {
    if (!relevantPage) return [];
    return buildVersionLabels(relevantPage.quarters || [], relevantPage.scrapes || []);
  }, [relevantPage]);

  // Convert labels to VersionInfo objects
  const versionInfosFromGraphQL = useMemo(() => {
    if (!relevantPage) return [];
    return versionLabels.map(v => {
      const qData = relevantPage.quarters?.find((q: any) => q.version === v.version);
      const hasPlans = qData ? Array.isArray(qData.ids) && qData.ids.length > 0 : true;
      return {
        version: v.version,
        quarter: v.label === 'Latest' ? 'Latest' : versionToQuarter(v.version).replace('Q', '-Q'),
        date: formatVersionDate(v.version),
        hasPlans
      } as VersionInfo;
    });
  }, [versionLabels, relevantPage]);

  // Set version infos when they change
  useEffect(() => {
    if (versionInfosFromGraphQL.length > 0) {
      setVersionInfos(versionInfosFromGraphQL);
      
      // Select the latest version by default (first one in the array)
      setSelectedVersions([versionInfosFromGraphQL[0].version]);
      
      setIsLoading(false);
    }
  }, [versionInfosFromGraphQL]);

  // Update loading state based on GraphQL loading
  useEffect(() => {
    setIsLoading(isGraphQLLoading);
  }, [isGraphQLLoading]);

  const handleVersionToggle = (version: string) => {
    setSelectedVersions(prev => {
      if (prev.includes(version)) {
        return prev.filter(v => v !== version);
      } else {
        return [...prev, version];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedVersions.length === versionInfos.filter(v => v.hasPlans).length) {
      // If all are selected, deselect all
      setSelectedVersions([]);
    } else {
      // Otherwise, select all versions with plans
      setSelectedVersions(versionInfos.filter(v => v.hasPlans).map(v => v.version));
    }
  };

  const handleSubmit = async () => {
    if (selectedVersions.length === 0) {
      toast({
        title: "No versions selected",
        description: "Please select at least one version to extract.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setResults(null);

    try {
      const result = await enqueueQuarterlyPriceExtractions(slug, subslug, force, false, selectedVersions);
      
      setResults(result);
      
      if (result.taskIds.length === 0) {
        toast({
          title: "No tasks created",
          description: "No valid quarterly versions found for this company.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Tasks created",
          description: `Created ${result.taskIds.length} price extraction ${result.taskIds.length === 1 ? 'task' : 'tasks'}.`,
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error creating tasks:', error);
      toast({
        title: "Error",
        description: "Failed to create price extraction tasks.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
      // Reset state when modal is closed
      setTimeout(() => {
        setResults(null);
      }, 300);
    }
  };

  const formatDate = (version: string) => {
    const formatted = formatVersionDate(version);
    if (formatted) return formatted;
    
    // Fallback - use the same utility function for consistency
    const fallback = formatVersionDateUS(version);
    return fallback || version;
  };

  const allSelected = versionInfos.filter(v => v.hasPlans).length > 0 && 
                      selectedVersions.length === versionInfos.filter(v => v.hasPlans).length;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Extract Quarterly Prices</DialogTitle>
          <DialogDescription>
            Select which quarterly versions to extract pricing data for {slug}
            {subslug ? `/${subslug}` : ''}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Force option */}
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <Label htmlFor="force" className="cursor-pointer">Force re-extraction</Label>
              <span className="text-xs text-muted-foreground">
                Override existing pricing data
              </span>
            </div>
            <Switch
              id="force"
              checked={force}
              onCheckedChange={setForce}
            />
          </div>

          {/* Force alerts */}
          {!force && (
            <Alert variant="warning" className="bg-yellow-50 border-yellow-100">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                Without forcing re-extraction, versions with existing pricing data will be skipped.
              </AlertDescription>
            </Alert>
          )}

          {force && (
            <Alert className="bg-blue-50 border-blue-100">
              <Info className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-800">
                Force mode is enabled. Existing pricing data will be overwritten.
              </AlertDescription>
            </Alert>
          )}

          {/* Version selection */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium">Available Versions</h3>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleSelectAll}
                disabled={isLoading || versionInfos.filter(v => v.hasPlans).length === 0}
                className="text-xs h-8"
              >
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : versionInfos.length === 0 ? (
              <Alert className="bg-gray-50 border-gray-100">
                <Info className="h-4 w-4 text-gray-600" />
                <AlertDescription className="text-gray-800">
                  No quarterly versions found for this company.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="max-h-60 overflow-y-auto border rounded-md">
                {versionInfos.map((versionInfo) => (
                  <div 
                    key={versionInfo.version} 
                    className={`flex items-center p-2 border-b last:border-b-0 ${!versionInfo.hasPlans ? 'bg-gray-50 opacity-70' : ''}`}
                  >
                    <Checkbox 
                      id={`version-${versionInfo.version}`}
                      checked={selectedVersions.includes(versionInfo.version)}
                      onCheckedChange={() => handleVersionToggle(versionInfo.version)}
                      disabled={!versionInfo.hasPlans}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <label 
                        htmlFor={`version-${versionInfo.version}`} 
                        className={`flex flex-col cursor-pointer ${!versionInfo.hasPlans ? 'text-gray-500' : ''}`}
                      >
                        <span className="font-medium text-sm flex items-center">
                          <Calendar className="h-3.5 w-3.5 mr-1.5" />
                          {versionInfo.quarter} <span className="text-gray-500 ml-1.5 text-xs">({formatDate(versionInfo.version)})</span>
                        </span>
                        {!versionInfo.hasPlans && (
                          <span className="text-xs text-red-500 flex items-center mt-0.5">
                            <X className="h-3 w-3 mr-1" />
                            No plans data available
                          </span>
                        )}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Results section */}
          {results && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <h4 className="text-sm font-medium mb-2">Task Results</h4>
              {results.taskIds.length === 0 ? (
                <p className="text-sm text-gray-600">No valid quarterly versions found.</p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-2">
                    Created {results.taskIds.length} tasks for the following versions:
                  </p>
                  <ul className="text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                    {results.versions.map((version) => (
                      <li key={version} className="flex items-center">
                        <Check className="h-3 w-3 text-green-500 mr-1" />
                        {version}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {results ? "Close" : "Cancel"}
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || selectedVersions.length === 0}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating Tasks...
              </>
            ) : (
              "Run Extracts"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ExtractQuarterlyPricesModal;
